import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { downloadFile, openFile } from "@/shared/api/files";
import { request } from "@/shared/api/http";
import { makeKeys } from "@/shared/api/queryKeys";
import {
  activitySchema,
  claimSchema,
  commentSchema,
  unitDetailSchema,
  unitSchema,
  type Unit,
  type UnitDetail,
} from "@/features/units/schemas";
import type { Availability } from "@/features/units/lib/filters";

export const unitKeys = makeKeys("units");
export const claimKeys = makeKeys("claims");

export function useUnits(enabled = true) {
  return useQuery({
    queryKey: unitKeys.lists(),
    enabled,
    queryFn: ({ signal }) => request("inventories", { schema: z.array(unitSchema), signal }),
  });
}

export function useUnit(id: number) {
  const queryClient = useQueryClient();
  return useQuery<UnitDetail>({
    queryKey: unitKeys.detail(id),
    queryFn: ({ signal }) =>
      request(`inventories/${String(id)}`, { schema: unitDetailSchema, signal }),
    // Open instantly from the list's copy while the full record loads.
    placeholderData: () =>
      queryClient.getQueryData<Unit[]>(unitKeys.lists())?.find((unit) => unit.id === id),
  });
}

export function useUnitActivities(id: number) {
  return useQuery({
    queryKey: [...unitKeys.detail(id), "activities"],
    queryFn: ({ signal }) =>
      request(`inventories/${String(id)}/activities`, { schema: z.array(activitySchema), signal }),
  });
}

export function useUnitComments(id: number) {
  return useQuery({
    queryKey: [...unitKeys.detail(id), "comments"],
    queryFn: ({ signal }) =>
      request(`inventories/${String(id)}/comments`, { schema: z.array(commentSchema), signal }),
  });
}

export function useClaims() {
  return useQuery({
    queryKey: claimKeys.lists(),
    queryFn: ({ signal }) => request("claims", { schema: z.array(claimSchema), signal }),
  });
}

/** Patch a unit in the list and detail caches at once (optimistic updates). */
function patchUnitCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  id: number,
  patch: Partial<Unit>,
) {
  queryClient.setQueryData<Unit[]>(unitKeys.lists(), (units) =>
    units?.map((unit) => (unit.id === id ? { ...unit, ...patch } : unit)),
  );
  queryClient.setQueryData<Unit>(unitKeys.detail(id), (unit) =>
    unit ? { ...unit, ...patch } : unit,
  );
}

function useUnitPatch<Vars>(
  path: (vars: Vars & { id: number }) => string,
  body: (vars: Vars) => Record<string, unknown>,
  optimistic: (vars: Vars) => Partial<Unit>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: Vars & { id: number }) =>
      request(path(vars), { method: "PATCH", json: body(vars), schema: z.unknown() }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: unitKeys.all });
      const previous = {
        list: queryClient.getQueryData<Unit[]>(unitKeys.lists()),
        detail: queryClient.getQueryData<Unit>(unitKeys.detail(vars.id)),
      };
      patchUnitCaches(queryClient, vars.id, optimistic(vars));
      return previous;
    },
    onError: (_error, vars, previous) => {
      queryClient.setQueryData(unitKeys.lists(), previous?.list);
      queryClient.setQueryData(unitKeys.detail(vars.id), previous?.detail);
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: unitKeys.detail(vars.id) });
    },
  });
}

export function useSetAvailability() {
  return useUnitPatch<{ status: Availability }>(
    ({ id }) => `inventories/${String(id)}/status`,
    ({ status }) => ({ status }),
    ({ status }) => ({ is_sold: status === "sold", is_reserved: status === "reserved" }),
  );
}

export function useSetTag() {
  return useUnitPatch<{ tag: string | null }>(
    ({ id }) => `inventories/${String(id)}/status-tags`,
    ({ tag }) => ({ status_tags: tag }),
    ({ tag }) => ({ status_tags: tag }),
  );
}

export interface UnitInput {
  developer_id: number;
  project_id: number;
  unit_code: string;
  unit_number: string;
  phase: string;
  unit_type: string;
  finishing: string;
  beds: number;
  bathroom: number;
  bua: number;
  size: number;
  land_area: string;
  total_price: number;
  dp: number;
  remaining: number;
  property_features: string;
  additional: string;
  notes: string;
  /** New photos; on edit they replace every current photo. */
  images: File[];
}

function unitForm(input: UnitInput) {
  const { images, ...fields } = input;
  return { ...fields, images };
}

export function useSaveUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: number; input: UnitInput }) => {
      // On edit, sending no photos keeps the current ones (an empty images[] is not sent).
      const fields = unitForm(input);
      return id === undefined
        ? request("inventories", { method: "POST", form: fields, schema: unitSchema })
        : request(`inventories/${String(id)}`, { method: "PUT", form: fields, schema: unitSchema });
    },
    onSuccess: (unit) => {
      queryClient.setQueryData(unitKeys.detail(unit.id), unit);
      void queryClient.invalidateQueries({ queryKey: unitKeys.all });
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      request(`inventories/${String(id)}`, { method: "DELETE", schema: z.unknown() }),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<Unit[]>(unitKeys.lists(), (units) =>
        units?.filter((unit) => unit.id !== id),
      );
      queryClient.removeQueries({ queryKey: unitKeys.detail(id) });
    },
  });
}

export function useImportUnits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      request("inventories/import", { method: "POST", form: { file }, schema: z.unknown() }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: unitKeys.all }),
  });
}

export function downloadOffer(id: number): Promise<string> {
  return downloadFile(`inventories/${String(id)}/generate-offer`);
}

export function previewOffer(id: number): Promise<void> {
  return openFile(`inventories/${String(id)}/generate-offer`);
}

export function useAddComment(unitId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comment: string) =>
      request(`inventories/${String(unitId)}/comments`, {
        method: "POST",
        json: { comment },
        schema: z.unknown(),
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: [...unitKeys.detail(unitId), "comments"] }),
  });
}

export function useDeleteComment(unitId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) =>
      request(`inventories/${String(unitId)}/comments/${String(commentId)}`, {
        method: "DELETE",
        schema: z.unknown(),
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: [...unitKeys.detail(unitId), "comments"] }),
  });
}

export function useDecideClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      claimId,
      status,
    }: {
      claimId: number;
      unitId: number;
      status: "approved" | "rejected";
    }) =>
      request(`claims/${String(claimId)}/status`, {
        method: "PATCH",
        json: { status },
        schema: z.unknown(),
      }),
    onSuccess: (_data, { unitId }) => {
      void queryClient.invalidateQueries({ queryKey: unitKeys.detail(unitId) });
      void queryClient.invalidateQueries({ queryKey: claimKeys.all });
    },
  });
}
